import { useState, useEffect } from 'react';
import { CardHeader, CardBody, Button, TabContent, TabPane, Alert } from 'reactstrap';
import renderField from './RenderField';

const RenderFormWithSteps = ({
    schema = [],
    formData,
    formErrors,
    handleChange,
    validateTabFields,
    handleSubmit,
    handleSaveDraft,
    onSaveDraft,
    hasUnsavedChanges,
    viewOnly,
    statusPermissions,
    applicationStatus
}) => {
    const [activeTabId, setActiveTabId] = useState(null); // Will be set based on first schema item
    const [activeTab, setActiveTab] = useState(null);
    const [isTabValid, setIsTabValid] = useState(false); // Track current tab validity
    const [isFormValid, setIsFormValid] = useState(false); // Track whole form validity
    const [alert, setAlert] = useState({ visible: false, message: '', color: 'success' }); // State for alert

    // Initialize with the first tab when schema loads
    useEffect(() => {
        if (schema.length > 0 && activeTabId === null) {
            // Always start with the first tab in the sorted array
            setActiveTabId(schema[0].id);
        }
    }, [schema, activeTabId]);

    // For loading the correct tab based on the tab ID
    useEffect(() => {
        const foundTab = schema.find(tab => tab.id === activeTabId);
        if (foundTab) {
            setActiveTab(foundTab);
        } else if (schema.length > 0) {
            // If the current activeTabId doesn't exist, default to the first tab
            const firstTab = schema[0];
            setActiveTabId(firstTab.id);
            setActiveTab(firstTab);
        }
    }, [activeTabId, schema]);

    // For validating the active tab's fields
    useEffect(() => {
        if (activeTab) {
            // Instructions page is always valid (no form fields)
            if (activeTab.isInstructionPage) {
                setIsTabValid(true);
            } else if (viewOnly) {
                // In view-only mode, consider all tabs as valid for navigation
                setIsTabValid(true);
            } else {
                const isValid = validateTabFields(activeTab);
                setIsTabValid(isValid);
            }
        }
    }, [activeTab, formData]);

    // For validating the entire form across all tabs
    useEffect(() => {
        if (viewOnly) {
            // In view-only mode, consider form as valid for navigation
            setIsFormValid(true);
        } else {
            const allTabsValid = schema
                .filter(tab => !tab.isInstructionPage) // Exclude instruction pages from validation
                .every((tab) => validateTabFields(tab));
            setIsFormValid(allTabsValid);
        }
    }, [formData, schema, viewOnly]);

    // Alert auto-dismiss
    useEffect(() => {
        if (alert.visible) {
            const timeout = setTimeout(() => setAlert({ ...alert, visible: false }), 3000);
            return () => clearTimeout(timeout);
        }
    }, [alert]);

    // Handle Save Draft with alert/notification (only for manual save)
    const handleSaveDraftWithAlert = async (e) => {
        try {
            // Use onSaveDraft if available, otherwise fallback to handleSaveDraft
            if (onSaveDraft) {
                await onSaveDraft(formData, true); // Pass true to show alert
            } else {
                await handleSaveDraft(e);
            }
            setAlert({ visible: true, message: 'Draft saved successfully!', color: 'success' });
        } catch (error) {
            setAlert({ visible: true, message: 'Failed to save draft. Please try again.', color: 'danger' });
        }
    };

    // Handle Save Draft silently (for next button auto-save)
    const handleSaveDraftSilently = async () => {
        try {
            // Use onSaveDraft if available, otherwise fallback to handleSaveDraft
            if (onSaveDraft) {
                await onSaveDraft(formData, false); // Pass false to suppress alert
            } else {
                await handleSaveDraft({ preventDefault: () => {} });
            }
        } catch (error) {
            console.error('Silent save draft error:', error);
            throw error; // Re-throw so goToNext can handle the error
        }
    };

    // Handle Submit with alert/notification
    const handleSubmitWithAlert = async (e) => {
        try {
            await handleSubmit(e);
            setAlert({ visible: true, message: 'Form submitted successfully!', color: 'success' });
        } catch (error) {
            setAlert({ visible: true, message: 'Form submission failed. Please try again.', color: 'danger' });
        }
    };

    // Function to go to the next tab
    const goToNext = async (e) => {
        e.preventDefault();
        
        // For instructions page, just go to next without saving
        if (activeTab && activeTab.isInstructionPage) {
            if (activeTabId !== null && activeTabId < schema[schema.length - 1]?.id) {
                setActiveTabId(activeTabId + 1);
            }
            return;
        }
        
        // For view-only mode, just navigate without saving
        if (viewOnly) {
            if (isTabValid && activeTabId !== null && activeTabId < schema[schema.length - 1]?.id) {
                setActiveTabId(activeTabId + 1);
            }
            return;
        }
        
        // OPTIMIZED: Only save if there are actual unsaved changes
        // This prevents unnecessary API calls and potential race conditions
        if (hasUnsavedChanges) {
            console.log('💾 Saving changes before navigation...');
            try {
                await handleSaveDraftSilently();
                console.log('✅ Save completed, proceeding to next step');
            } catch (error) {
                setAlert({ visible: true, message: 'Failed to save data. Please correct and try again.', color: 'danger' });
                return; // Don't proceed to next tab if save failed
            }
        } else {
            console.log('⏭️ No unsaved changes, skipping save and navigating directly');
        }
        
        if (isTabValid && activeTabId !== null && activeTabId < schema[schema.length - 1]?.id) {
            setActiveTabId(activeTabId + 1);
        }
    };

    // Function to go to the previous tab
    const goToPrev = (e) => {
        e.preventDefault();
        if (activeTabId !== null && activeTabId > schema[0]?.id) { // Use the first schema item's id instead of hardcoded 1
            setActiveTabId(activeTabId - 1);
        }
    };

    return (
        <div id="basic-pills-wizard" className="twitter-bs-wizard">
            <CardHeader>
                <h4 className="card-title mb-0">{activeTab ? activeTab.title : ''}</h4>
            </CardHeader>
            <CardBody>

                {/* Alert Component */}
                <Alert color={alert.color} isOpen={alert.visible}>
                    {alert.message}
                </Alert>

                {/* 
                <div className="twitter-bs-wizard-nav nav nav-pills nav-justified">
                    {schema.map((tab) => (
                        <NavItem key={tab.id}>
                            <NavLink
                                className={classnames({ active: activeTabId === tab.id })}
                                onClick={() => setActiveTabId(tab.id)}
                            >
                                <span className="step-number">{tab.step}</span>
                            </NavLink>
                        </NavItem>
                    ))}
                </div>
*/}
                <TabContent activeTab={activeTabId}>
                    {activeTab && (
                        <TabPane tabId={activeTab.id}>
                            {/* Special handling for Instructions page */}
                            {activeTab.isInstructionPage ? (
                                <div style={{ minHeight: '400px', padding: '20px 0' }}>
                                    <p>Please read the filing requirements carefully. Failing to meet these&nbsp;requirements will delay the processing of your application.</p>
                                    <ul>
                                        <li>Please fill out the details correctly.</li>
                                        <li>Click Next and fill out each section.</li>
                                        <li>Ensure you have all the following documents (<strong>attested copy</strong> by Gazetted/Appropriate officer) ready to be uploaded. You will not be able to complete the application without them.</li>
                                    </ul>
                                    <ol>
                                        <li>Photo copy of Aadhar card</li>
                                        <li>Upload Passport Size Photo (2" x 2") of Student</li>
                                        <li>Bonafide certificate or Tuition Fees Receipt</li>
                                        <li>Income certificate authorized by Government (Sarpanch, Mamlatdar etc.)</li>
                                        <li>Previous year/exam result</li>
                                        <li>Current year school/college fee detailed</li>
                                        <li>Bank Passbook or Cancel check</li>
                                    </ol>
                                </div>
                            ) : (
                                /* Regular form sections */
                                <>
                                    {activeTab.title === "Community References" && activeTab.description ?
                                        <div className="alert alert-info mb-3" role="alert">
                                            <strong>IMPORTANT :</strong>
                                            <div style={{ whiteSpace: 'pre-line', marginTop: '8px' }}>
                                                {activeTab.description}
                                            </div>
                                        </div> : activeTab.description && <p>{activeTab.description}</p>
                                    }

                                    {/* Display UI-only instructions if available */}
                                    {activeTab.uiInstructions && (
                                        <div className="alert alert-warning mb-3" role="alert">
                                            <strong><i className="fas fa-info-circle me-2"></i>Instructions:</strong>
                                            <div 
                                                style={{ whiteSpace: 'pre-line', marginTop: '8px' }}
                                                dangerouslySetInnerHTML={{ __html: activeTab.uiInstructions }}
                                            />
                                        </div>
                                    )}

                                    {activeTab.haveSections ? (
                                        activeTab.sections.map((section, idx) => (
                                            <div key={idx}>
                                                <h5>{section.title}</h5>
                                                {section.fields.map((field, i) => (
                                                    <div key={i}>
                                                        {renderField(field, handleChange, formData, null, viewOnly)}
                                                        {formErrors[field.name] && (
                                                            <div className="text-danger">{formErrors[field.name]}</div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ))
                                    ) : (
                                        <div>
                                            <p>{activeTab.description}</p>
                                            {activeTab.fields.map((field, idx) => (
                                                <div key={idx}>
                                                    {renderField(field, handleChange, formData, null, viewOnly)}
                                                    {formErrors[field.name] && (
                                                        <div className="text-danger">{formErrors[field.name]}</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </TabPane>
                    )}
                </TabContent>

                <div className="d-flex justify-content-between mt-3">
                    <Button color="secondary" disabled={activeTabId === null || activeTabId === schema[0]?.id} onClick={goToPrev}>
                        Previous
                    </Button>

                    {/* Hide Save Draft button for instructions page, view-only mode, or when not allowed by status */}
                    {activeTab && !activeTab.isInstructionPage && !viewOnly && statusPermissions?.canSaveDraft && (
                        <Button
                            color="warning"
                            className="mx-auto"
                            disabled={!isTabValid || !hasUnsavedChanges} // Enable only if valid and changes made
                            onClick={handleSaveDraftWithAlert}
                        >
                            Save Draft
                        </Button>
                    )}

                    { activeTabId === schema[schema.length - 1]?.id ? (
                        <Button
                            color="primary"
                            disabled={!isFormValid || viewOnly || !statusPermissions?.canSubmit} // Enable only if form is valid and submission is allowed
                            onClick={handleSubmitWithAlert}
                        >
                            Submit
                        </Button>
                    ) : (
                        <Button
                            color="primary"
                            disabled={!isTabValid} // Enable Next only if current tab is valid - remove viewOnly restriction for navigation
                            onClick={goToNext}
                        >
                            Next
                        </Button>
                    )}
                </div>
            </CardBody>
        </div>
    );
};

export default RenderFormWithSteps;
